/**
 * AdminUsers — admin page for user management.
 *
 * Features:
 * - Filter chips: All / Coaches / Athletes / Admins / Inactive
 * - Search by email or display_name (debounced)
 * - Users table with columns: Display Name | Email | Role | Subscription | Coach Tier | Active | Actions
 * - Edit modal with all editable fields (role, subscription, limits, etc.)
 * - Activate / Deactivate quick action per row
 * - Pagination if > 50 rows
 *
 * Route: /admin/users (protected by AdminGuard in App.tsx)
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import {
  MagnifyingGlassIcon,
  PencilSquareIcon,
  XMarkIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
} from '@heroicons/react/24/outline';
import Button from '@/components/Button';
import Input from '@/components/ui/Input';
import Modal from '@/components/ui/Modal';
import ConfirmationDialog from '@/components/ui/ConfirmationDialog';
import {
  getUsers,
  updateUser,
  approveUser,
  getUserDetail,
  getAllChannels,
  resetEvalUsage,
} from '@/services/adminApi';
import type { AdminUser, AdminUserDetail, UpdateUserPayload, ApproveUserPayload, AdminChannel } from '@/types/admin';
import { formatDate } from '@/utils/formatters';

// ─── Helpers ──────────────────────────────────────────────────────────────────

// ─── Toast ────────────────────────────────────────────────────────────────────

interface ToastProps {
  message: string;
  type: 'success' | 'error';
  onDismiss: () => void;
}

function Toast({ message, type, onDismiss }: ToastProps) {
  const isError = type === 'error';
  return (
    <div
      role="alert"
      aria-live="polite"
      style={{
        position: 'fixed',
        bottom: '24px',
        right: '24px',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'flex-start',
        gap: '12px',
        padding: '14px 16px',
        borderRadius: '10px',
        backgroundColor: isError ? 'rgba(248,113,113,0.12)' : 'rgba(52,211,153,0.12)',
        border: `1px solid ${isError ? 'rgba(248,113,113,0.35)' : 'rgba(52,211,153,0.35)'}`,
        boxShadow: '0 4px 24px rgba(0,0,0,0.4)',
        maxWidth: '420px',
        color: 'var(--color-text-primary)',
        fontSize: '14px',
      }}
    >
      {isError ? (
        <ExclamationCircleIcon
          style={{ width: '18px', height: '18px', flexShrink: 0, color: '#F87171', marginTop: '1px' }}
        />
      ) : (
        <CheckCircleIcon
          style={{ width: '18px', height: '18px', flexShrink: 0, color: '#34D399', marginTop: '1px' }}
        />
      )}
      <span style={{ flex: 1, lineHeight: '1.5' }}>{message}</span>
      <button
        type="button"
        aria-label="Dismiss notification"
        onClick={onDismiss}
        style={{
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          color: 'var(--color-text-muted)',
          padding: '0',
          marginTop: '1px',
        }}
      >
        <XMarkIcon style={{ width: '16px', height: '16px' }} />
      </button>
    </div>
  );
}

// ─── Role badge ───────────────────────────────────────────────────────────────

function RoleBadge({ role }: { role: string }) {
  const config: Record<string, { color: string; bg: string }> = {
    admin: { color: 'var(--color-accent-gold)', bg: 'rgba(244,201,93,0.12)' },
    coach: { color: 'var(--color-accent-blue, #60A5FA)', bg: 'rgba(96,165,250,0.12)' },
    athlete: { color: 'var(--color-text-secondary)', bg: 'rgba(168,180,199,0.10)' },
  };
  const c = config[role] ?? config['athlete'];
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '2px 8px',
        borderRadius: '6px',
        fontSize: '12px',
        fontWeight: 500,
        color: c.color,
        backgroundColor: c.bg,
        textTransform: 'capitalize',
      }}
    >
      {role}
    </span>
  );
}

// ─── Filter chips ─────────────────────────────────────────────────────────────

type FilterChipValue = 'all' | 'coach' | 'athlete' | 'admin' | 'inactive' | 'pending' | 'owner_only' | 'paying_only';

function FilterChips({
  value,
  onChange,
}: {
  value: FilterChipValue;
  onChange: (v: FilterChipValue) => void;
}) {
  const chips: { value: FilterChipValue; label: string }[] = [
    { value: 'all', label: 'All' },
    { value: 'pending', label: 'Pending approval' },
    { value: 'coach', label: 'Coaches' },
    { value: 'athlete', label: 'Athletes' },
    { value: 'admin', label: 'Admins' },
    { value: 'inactive', label: 'Inactive' },
    { value: 'owner_only', label: 'Channel owners' },
    { value: 'paying_only', label: 'Paying owners' },
  ];

  return (
    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
      {chips.map((chip) => {
        const active = chip.value === value;
        return (
          <button
            key={chip.value}
            type="button"
            onClick={() => onChange(chip.value)}
            style={{
              padding: '5px 14px',
              borderRadius: '20px',
              border: `1px solid ${active ? 'var(--color-accent)' : 'var(--color-border-subtle)'}`,
              backgroundColor: active ? 'rgba(59,130,246,0.12)' : 'transparent',
              color: active ? 'var(--color-accent)' : 'var(--color-text-secondary)',
              fontSize: '13px',
              fontWeight: 500,
              cursor: 'pointer',
              transition: 'all 120ms ease',
            }}
          >
            {chip.label}
          </button>
        );
      })}
    </div>
  );
}

// ─── Ownership badge ──────────────────────────────────────────────────────────

function OwnershipBadge({ user }: { user: AdminUser }) {
  if (user.is_paying_owner) {
    return (
      <span
        style={{
          display: 'inline-block',
          padding: '2px 7px',
          borderRadius: '6px',
          fontSize: '11px',
          fontWeight: 600,
          color: '#34D399',
          backgroundColor: 'rgba(52,211,153,0.12)',
          border: '1px solid rgba(52,211,153,0.25)',
          marginLeft: '6px',
          verticalAlign: 'middle',
          whiteSpace: 'nowrap',
        }}
      >
        Paying owner
      </span>
    );
  }
  if (user.owns_channel) {
    return (
      <span
        style={{
          display: 'inline-block',
          padding: '2px 7px',
          borderRadius: '6px',
          fontSize: '11px',
          fontWeight: 500,
          color: 'var(--color-text-muted)',
          backgroundColor: 'rgba(168,180,199,0.08)',
          border: '1px solid rgba(168,180,199,0.18)',
          marginLeft: '6px',
          verticalAlign: 'middle',
          whiteSpace: 'nowrap',
        }}
      >
        Owner
      </span>
    );
  }
  return null;
}

// ─── User detail modal ────────────────────────────────────────────────────────

interface UserDetailModalProps {
  user: AdminUser;
  onClose: () => void;
}

function UserDetailModal({ user, onClose }: UserDetailModalProps) {
  const [detail, setDetail] = useState<AdminUserDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    getUserDetail(user.id)
      .then((data) => {
        if (!cancelled) {
          setDetail(data);
          setLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setError('Failed to load user details.');
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [user.id]);

  const sectionLabel: React.CSSProperties = {
    fontSize: '11px',
    fontWeight: 600,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: 'var(--color-text-muted)',
    marginBottom: '10px',
    marginTop: '20px',
  };

  const statRow: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '7px 0',
    borderBottom: '1px solid var(--color-border-subtle)',
    fontSize: '13px',
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Details for ${user.email}`}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 10000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
      }}
    >
      {/* Backdrop */}
      <div
        style={{ position: 'absolute', inset: 0, backgroundColor: 'rgba(6,9,15,0.8)' }}
        onClick={onClose}
      />

      {/* Modal panel */}
      <div
        style={{
          position: 'relative',
          width: '100%',
          maxWidth: '640px',
          maxHeight: '88vh',
          overflowY: 'auto',
          backgroundColor: 'var(--color-dark-elevated-2)',
          border: '1px solid var(--color-border-strong)',
          borderRadius: '16px',
          padding: '28px',
          boxShadow: '0 24px 64px rgba(0,0,0,0.6)',
        }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            marginBottom: '20px',
          }}
        >
          <div>
            <h2
              style={{
                fontFamily: 'var(--font-display)',
                fontWeight: 600,
                fontSize: '20px',
                color: 'var(--color-text-primary)',
                margin: 0,
              }}
            >
              User Details
            </h2>
            <p style={{ marginTop: '4px', fontSize: '13px', color: 'var(--color-text-muted)' }}>
              {user.email}
            </p>
          </div>
          <button
            type="button"
            aria-label="Close details"
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: 'var(--color-text-muted)',
              padding: '4px',
            }}
          >
            <XMarkIcon style={{ width: '20px', height: '20px' }} />
          </button>
        </div>

        {loading && (
          <p style={{ color: 'var(--color-text-muted)', fontSize: '14px', textAlign: 'center', padding: '32px 0' }}>
            Loading…
          </p>
        )}

        {error && !loading && (
          <div
            style={{
              padding: '10px 14px',
              borderRadius: '8px',
              backgroundColor: 'rgba(248,113,113,0.1)',
              border: '1px solid rgba(248,113,113,0.25)',
              color: '#F87171',
              fontSize: '13px',
            }}
          >
            {error}
          </div>
        )}

        {detail && !loading && (
          <>
            {/* Overview stats */}
            <p style={sectionLabel}>Overview</p>
            <div style={statRow}>
              <span style={{ color: 'var(--color-text-muted)' }}>Total evaluations</span>
              <span style={{ color: 'var(--color-text-primary)', fontWeight: 500 }}>
                {detail.eval_count.toLocaleString()}
              </span>
            </div>
            <div style={statRow}>
              <span style={{ color: 'var(--color-text-muted)' }}>Last login</span>
              <span style={{ color: 'var(--color-text-primary)', fontWeight: 500 }}>
                {detail.last_login ? formatDate(detail.last_login, 'MMM d, yyyy HH:mm') : 'Never'}
              </span>
            </div>

            {/* Channel memberships */}
            <p style={sectionLabel}>
              Channel memberships
              <span
                style={{
                  marginLeft: '8px',
                  fontSize: '11px',
                  fontWeight: 500,
                  color: 'var(--color-text-muted)',
                  textTransform: 'none',
                  letterSpacing: 0,
                }}
              >
                ({detail.channel_memberships.length})
              </span>
            </p>

            {detail.channel_memberships.length === 0 ? (
              <p style={{ fontSize: '13px', color: 'var(--color-text-muted)', fontStyle: 'italic' }}>
                Not a member of any channel.
              </p>
            ) : (
              <div
                style={{
                  border: '1px solid var(--color-border-subtle)',
                  borderRadius: '10px',
                  overflow: 'hidden',
                }}
              >
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                  <thead>
                    <tr style={{ backgroundColor: 'var(--color-dark-elevated-1)' }}>
                      <th
                        style={{
                          padding: '8px 12px',
                          textAlign: 'left',
                          fontWeight: 600,
                          letterSpacing: '0.05em',
                          textTransform: 'uppercase' as const,
                          color: 'var(--color-text-muted)',
                          fontSize: '10px',
                          borderBottom: '1px solid var(--color-border-subtle)',
                        }}
                      >
                        Channel
                      </th>
                      <th
                        style={{
                          padding: '8px 12px',
                          textAlign: 'left',
                          fontWeight: 600,
                          letterSpacing: '0.05em',
                          textTransform: 'uppercase' as const,
                          color: 'var(--color-text-muted)',
                          fontSize: '10px',
                          borderBottom: '1px solid var(--color-border-subtle)',
                        }}
                      >
                        Role
                      </th>
                      <th
                        style={{
                          padding: '8px 12px',
                          textAlign: 'left',
                          fontWeight: 600,
                          letterSpacing: '0.05em',
                          textTransform: 'uppercase' as const,
                          color: 'var(--color-text-muted)',
                          fontSize: '10px',
                          borderBottom: '1px solid var(--color-border-subtle)',
                        }}
                      >
                        Seat source
                      </th>
                      <th
                        style={{
                          padding: '8px 12px',
                          textAlign: 'left',
                          fontWeight: 600,
                          letterSpacing: '0.05em',
                          textTransform: 'uppercase' as const,
                          color: 'var(--color-text-muted)',
                          fontSize: '10px',
                          borderBottom: '1px solid var(--color-border-subtle)',
                        }}
                      >
                        Funded by
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {detail.channel_memberships.map((m) => (
                      <tr key={m.channel_id}>
                        <td
                          style={{
                            padding: '9px 12px',
                            borderBottom: '1px solid var(--color-border-subtle)',
                            color: 'var(--color-text-primary)',
                          }}
                        >
                          <div style={{ fontWeight: 500 }}>
                            {m.channel_name ?? <span style={{ color: 'var(--color-text-muted)', fontStyle: 'italic' }}>Unnamed</span>}
                          </div>
                          {m.channel_slug && (
                            <div style={{ color: 'var(--color-text-muted)', fontSize: '11px', marginTop: '1px' }}>
                              /{m.channel_slug}
                            </div>
                          )}
                        </td>
                        <td
                          style={{
                            padding: '9px 12px',
                            borderBottom: '1px solid var(--color-border-subtle)',
                            color: 'var(--color-text-secondary)',
                            textTransform: 'capitalize',
                          }}
                        >
                          {m.member_role}
                        </td>
                        <td
                          style={{
                            padding: '9px 12px',
                            borderBottom: '1px solid var(--color-border-subtle)',
                            color: 'var(--color-text-secondary)',
                          }}
                        >
                          {m.seat_source ?? <span style={{ color: 'var(--color-text-muted)' }}>—</span>}
                        </td>
                        <td
                          style={{
                            padding: '9px 12px',
                            borderBottom: '1px solid var(--color-border-subtle)',
                            color: 'var(--color-text-muted)',
                            fontFamily: 'monospace',
                            fontSize: '11px',
                          }}
                        >
                          {m.funded_by_user_id ? m.funded_by_user_id.slice(0, 8) + '…' : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {/* Footer */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '24px' }}>
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </div>
  );
}

// ─── Edit User modal ──────────────────────────────────────────────────────────

interface EditModalProps {
  user: AdminUser;
  onClose: () => void;
  onSaved: (updated: AdminUser) => void;
}

function EditUserModal({ user, onClose, onSaved }: EditModalProps) {
  const [displayName, setDisplayName] = useState(user.display_name ?? '');
  const [role, setRole] = useState<'coach' | 'athlete' | 'admin'>(user.role);
  const [subTier, setSubTier] = useState(user.subscription_tier?.toString() ?? '');
  const [coachTier, setCoachTier] = useState(user.coach_tier ?? '');
  const [evalLimit, setEvalLimit] = useState(user.monthly_eval_limit?.toString() ?? '');
  const [invLimit, setInvLimit] = useState(user.monthly_invitations_limit?.toString() ?? '');
  const [isActive, setIsActive] = useState(user.is_active);
  // YouTube-import override: 'grant' | 'deny' | 'default' (tier rule).
  const initialYoutubeAccess: 'grant' | 'deny' | 'default' =
    user.youtube_import_enabled === true
      ? 'grant'
      : user.youtube_import_enabled === false
        ? 'deny'
        : 'default';
  const [youtubeAccess, setYoutubeAccess] = useState<'grant' | 'deny' | 'default'>(
    initialYoutubeAccess,
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    const payload: UpdateUserPayload = {};

    if (displayName !== (user.display_name ?? '')) payload.display_name = displayName || undefined;
    if (role !== user.role) payload.role = role;
    if (isActive !== user.is_active) payload.is_active = isActive;

    const parsedSubTier = subTier !== '' ? parseInt(subTier, 10) : null;
    if (parsedSubTier !== user.subscription_tier) payload.subscription_tier = parsedSubTier;

    const parsedEvalLimit = evalLimit !== '' ? parseInt(evalLimit, 10) : null;
    if (parsedEvalLimit !== user.monthly_eval_limit) payload.monthly_eval_limit = parsedEvalLimit;

    const parsedInvLimit = invLimit !== '' ? parseInt(invLimit, 10) : null;
    if (parsedInvLimit !== user.monthly_invitations_limit) payload.monthly_invitations_limit = parsedInvLimit;

    const effectiveCoachTier = coachTier || null;
    if (effectiveCoachTier !== user.coach_tier) payload.coach_tier = effectiveCoachTier;

    if (youtubeAccess !== initialYoutubeAccess) payload.youtube_import_access = youtubeAccess;

    if (Object.keys(payload).length === 0) {
      onClose();
      return;
    }

    try {
      const updated = await updateUser(user.id, payload);
      onSaved(updated);
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ??
        'Failed to save changes.';
      setError(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally {
      setSaving(false);
    }
  };

  const selectStyle: React.CSSProperties = {
    width: '100%',
    height: '40px',
    borderRadius: '10px',
    border: '1px solid var(--color-border-subtle)',
    backgroundColor: 'var(--color-dark-elevated-2)',
    color: 'var(--color-text-primary)',
    padding: '0 12px',
    fontSize: '14px',
    outline: 'none',
  };

  const labelStyle: React.CSSProperties = {
    display: 'block',
    fontSize: '14px',
    fontWeight: 500,
    color: 'var(--color-text-secondary)',
    marginBottom: '4px',
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Edit user ${user.email}`}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 10000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
      }}
    >
      {/* Backdrop */}
      <div
        style={{ position: 'absolute', inset: 0, backgroundColor: 'rgba(6,9,15,0.8)' }}
        onClick={onClose}
      />

      {/* Modal panel */}
      <div
        style={{
          position: 'relative',
          width: '100%',
          maxWidth: '560px',
          maxHeight: '90vh',
          overflowY: 'auto',
          backgroundColor: 'var(--color-dark-elevated-2)',
          border: '1px solid var(--color-border-strong)',
          borderRadius: '16px',
          padding: '28px',
          boxShadow: '0 24px 64px rgba(0,0,0,0.6)',
        }}
      >
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
          <div>
            <h2
              style={{
                fontFamily: 'var(--font-display)',
                fontWeight: 600,
                fontSize: '20px',
                color: 'var(--color-text-primary)',
                margin: 0,
              }}
            >
              Edit User
            </h2>
            <p style={{ marginTop: '4px', fontSize: '13px', color: 'var(--color-text-muted)' }}>
              {user.email}
            </p>
          </div>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: 'var(--color-text-muted)',
              padding: '4px',
            }}
          >
            <XMarkIcon style={{ width: '20px', height: '20px' }} />
          </button>
        </div>

        {error && (
          <div
            style={{
              padding: '10px 14px',
              borderRadius: '8px',
              backgroundColor: 'rgba(248,113,113,0.1)',
              border: '1px solid rgba(248,113,113,0.25)',
              color: '#F87171',
              fontSize: '13px',
              marginBottom: '20px',
            }}
          >
            {error}
          </div>
        )}

        {/* Fields */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {/* Display name */}
          <Input
            label="Display name"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="Leave blank to clear"
          />

          {/* Role — admins cannot be set here */}
          <div>
            <label style={labelStyle}>Role</label>
            <select value={role} onChange={(e) => setRole(e.target.value as 'coach' | 'athlete' | 'admin')} style={selectStyle}>
              <option value="coach">Coach</option>
              <option value="athlete">Athlete</option>
              {/* Admins can only be created via CLI; show option read-only when user is admin */}
              {user.role === 'admin' && <option value="admin">Admin</option>}
            </select>
            {user.role === 'admin' && (
              <p style={{ fontSize: '12px', color: 'var(--color-text-muted)', marginTop: '4px' }}>
                Admin role cannot be changed here — use CLI.
              </p>
            )}
          </div>

          {/* Subscription tier — billing tier (matches the catalog) */}
          <div>
            <label style={labelStyle}>Subscription (billing) tier</label>
            <select value={subTier} onChange={(e) => setSubTier(e.target.value)} style={selectStyle}>
              <option value="">No subscription tier</option>
              <option value="1">Tier 1 — Solo</option>
              <option value="2">Tier 2 — Academy (Team)</option>
              <option value="3">Tier 3 — Enterprise</option>
            </select>
          </div>

          {/* Coach tier (visible when role=coach) — the access control that
              governs template / seat / token LIMITS. */}
          {role === 'coach' && (
            <div>
              <label style={labelStyle}>Coach tier (governs template / seat / token limits)</label>
              <select value={coachTier} onChange={(e) => setCoachTier(e.target.value)} style={selectStyle}>
                <option value="">No coach tier</option>
                <option value="solo">Solo</option>
                <option value="team">Team</option>
                <option value="enterprise">Enterprise</option>
              </select>
            </div>
          )}

          {/* YouTube transcript-import access (visible when role=coach) */}
          {role === 'coach' && (
            <div>
              <label style={labelStyle}>YouTube transcript import</label>
              <select
                value={youtubeAccess}
                onChange={(e) =>
                  setYoutubeAccess(e.target.value as 'grant' | 'deny' | 'default')
                }
                style={selectStyle}
              >
                <option value="default">Default (enterprise tier only)</option>
                <option value="grant">Grant (negotiated access)</option>
                <option value="deny">Deny (block)</option>
              </select>
            </div>
          )}

          {/* Monthly evaluation limit */}
          <Input
            label="Monthly evaluation limit (leave blank for unlimited)"
            type="number"
            min={0}
            value={evalLimit}
            onChange={(e) => setEvalLimit(e.target.value)}
            placeholder="Unlimited"
          />

          {/* Monthly invitations limit (coach only) */}
          {role === 'coach' && (
            <Input
              label="Monthly invitations limit (leave blank for unlimited)"
              type="number"
              min={0}
              value={invLimit}
              onChange={(e) => setInvLimit(e.target.value)}
              placeholder="Unlimited"
            />
          )}

          {/* Active toggle */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <button
              type="button"
              role="switch"
              aria-checked={isActive}
              onClick={() => setIsActive((v) => !v)}
              style={{
                width: '44px',
                height: '24px',
                borderRadius: '12px',
                border: 'none',
                cursor: 'pointer',
                backgroundColor: isActive ? 'var(--color-accent)' : 'var(--color-border-strong)',
                position: 'relative',
                flexShrink: 0,
                transition: 'background-color 150ms ease',
              }}
            >
              <span
                style={{
                  position: 'absolute',
                  top: '2px',
                  left: isActive ? '22px' : '2px',
                  width: '20px',
                  height: '20px',
                  borderRadius: '50%',
                  backgroundColor: '#fff',
                  transition: 'left 150ms ease',
                }}
              />
            </button>
            <span style={{ fontSize: '14px', color: 'var(--color-text-secondary)' }}>
              {isActive ? 'Active' : 'Inactive (account disabled)'}
            </span>
          </div>
        </div>

        {/* Footer buttons */}
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '28px' }}>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save changes'}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ─── Approve User modal ───────────────────────────────────────────────────────

interface ApproveModalProps {
  user: AdminUser;
  onClose: () => void;
  onApproved: (updated: AdminUser) => void;
}

function ApproveUserModal({ user, onClose, onApproved }: ApproveModalProps) {
  const [subTier, setSubTier] = useState(user.subscription_tier?.toString() ?? '');
  const [coachTier, setCoachTier] = useState(user.coach_tier ?? '');
  const [evalLimit, setEvalLimit] = useState(user.monthly_eval_limit?.toString() ?? '');
  const [invLimit, setInvLimit] = useState(user.monthly_invitations_limit?.toString() ?? '0');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleApprove = async () => {
    setSaving(true);
    setError(null);

    const payload: ApproveUserPayload = {};

    const parsedSubTier = subTier !== '' ? parseInt(subTier, 10) : null;
    if (parsedSubTier !== null) payload.subscription_tier = parsedSubTier;

    const parsedEvalLimit = evalLimit !== '' ? parseInt(evalLimit, 10) : null;
    payload.monthly_eval_limit = parsedEvalLimit;

    const parsedInvLimit = invLimit !== '' ? parseInt(invLimit, 10) : 0;
    payload.monthly_invitations_limit = parsedInvLimit;

    if (user.role === 'coach' && coachTier) {
      payload.coach_tier = coachTier || null;
    }

    try {
      const updated = await approveUser(user.id, payload);
      onApproved(updated);
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ??
        'Failed to approve user.';
      setError(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally {
      setSaving(false);
    }
  };

  const selectStyle: React.CSSProperties = {
    width: '100%',
    height: '40px',
    borderRadius: '10px',
    border: '1px solid var(--color-border-subtle)',
    backgroundColor: 'var(--color-dark-elevated-2)',
    color: 'var(--color-text-primary)',
    padding: '0 12px',
    fontSize: '14px',
    outline: 'none',
  };

  const labelStyle: React.CSSProperties = {
    display: 'block',
    fontSize: '14px',
    fontWeight: 500,
    color: 'var(--color-text-secondary)',
    marginBottom: '4px',
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Approve user ${user.email}`}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 10000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
      }}
    >
      {/* Backdrop */}
      <div
        style={{ position: 'absolute', inset: 0, backgroundColor: 'rgba(6,9,15,0.8)' }}
        onClick={onClose}
      />

      {/* Modal panel */}
      <div
        style={{
          position: 'relative',
          width: '100%',
          maxWidth: '520px',
          maxHeight: '90vh',
          overflowY: 'auto',
          backgroundColor: 'var(--color-dark-elevated-2)',
          border: '1px solid var(--color-border-strong)',
          borderRadius: '16px',
          padding: '28px',
          boxShadow: '0 24px 64px rgba(0,0,0,0.6)',
        }}
      >
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
          <div>
            <h2
              style={{
                fontFamily: 'var(--font-display)',
                fontWeight: 600,
                fontSize: '20px',
                color: 'var(--color-text-primary)',
                margin: 0,
              }}
            >
              Approve User
            </h2>
            <p style={{ marginTop: '4px', fontSize: '13px', color: 'var(--color-text-muted)' }}>
              {user.email} ({user.role})
            </p>
          </div>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: 'var(--color-text-muted)',
              padding: '4px',
            }}
          >
            <XMarkIcon style={{ width: '20px', height: '20px' }} />
          </button>
        </div>

        {error && (
          <div
            style={{
              padding: '10px 14px',
              borderRadius: '8px',
              backgroundColor: 'rgba(248,113,113,0.1)',
              border: '1px solid rgba(248,113,113,0.25)',
              color: '#F87171',
              fontSize: '13px',
              marginBottom: '20px',
            }}
          >
            {error}
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {/* Subscription tier */}
          <div>
            <label style={labelStyle}>Subscription tier</label>
            <select value={subTier} onChange={(e) => setSubTier(e.target.value)} style={selectStyle}>
              <option value="">No subscription tier</option>
              <option value="1">Tier 1 — Standard</option>
              <option value="2">Tier 2 — Premium</option>
            </select>
          </div>

          {/* Coach tier (visible when role=coach) */}
          {user.role === 'coach' && (
            <div>
              <label style={labelStyle}>Coach tier</label>
              <select value={coachTier} onChange={(e) => setCoachTier(e.target.value)} style={selectStyle}>
                <option value="">No coach tier</option>
                <option value="solo">Solo</option>
                <option value="team">Team</option>
                <option value="enterprise">Enterprise</option>
              </select>
            </div>
          )}

          {/* Monthly evaluation limit */}
          <Input
            label="Monthly evaluation limit (leave blank for unlimited)"
            type="number"
            min={0}
            value={evalLimit}
            onChange={(e) => setEvalLimit(e.target.value)}
            placeholder="Unlimited"
          />

          {/* Monthly invitations limit */}
          <Input
            label="Monthly invitations limit"
            type="number"
            min={0}
            value={invLimit}
            onChange={(e) => setInvLimit(e.target.value)}
            placeholder="0"
          />
        </div>

        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '28px' }}>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleApprove} disabled={saving}>
            {saving ? 'Approving…' : 'Approve account'}
          </Button>
        </div>
      </div>
    </div>
  );
}

// TESTING-PHASE TOOL — rides the legacy monthly_eval_limit system; delete when the credit ledger fully replaces it
// ─── Set eval-limit modal ──────────────────────────────────────────────────────

interface SetLimitModalProps {
  user: AdminUser;
  onClose: () => void;
  onSaved: (updated: AdminUser) => void;
}

function SetLimitModal({ user, onClose, onSaved }: SetLimitModalProps) {
  const [limit, setLimit] = useState(user.monthly_eval_limit?.toString() ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    // Blank = unlimited (null). Otherwise a non-negative integer.
    const trimmed = limit.trim();
    let parsed: number | null;
    if (trimmed === '') {
      parsed = null;
    } else {
      const n = parseInt(trimmed, 10);
      if (isNaN(n) || n < 0) {
        setError('Enter a valid number (0 or more), or leave blank for unlimited.');
        setSaving(false);
        return;
      }
      parsed = n;
    }
    try {
      const updated = await updateUser(user.id, { monthly_eval_limit: parsed });
      onSaved(updated);
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ??
        'Failed to update evaluation limit.';
      setError(typeof msg === 'string' ? msg : JSON.stringify(msg));
      setSaving(false);
    }
  };

  return (
    <Modal isOpen onClose={onClose} title="Set evaluation limit" size="sm">
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <p style={{ fontSize: '13px', color: 'var(--color-text-muted)', margin: 0 }}>
          {user.email}
        </p>
        {error && (
          <div
            style={{
              padding: '10px 14px',
              borderRadius: '8px',
              backgroundColor: 'rgba(248,113,113,0.1)',
              border: '1px solid rgba(248,113,113,0.25)',
              color: '#F87171',
              fontSize: '13px',
            }}
          >
            {error}
          </div>
        )}
        <Input
          label="Monthly evaluation limit (leave blank for unlimited)"
          type="number"
          min={0}
          value={limit}
          onChange={(e) => setLimit(e.target.value)}
          placeholder="Unlimited"
        />
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save limit'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

// ─── Main page component ──────────────────────────────────────────────────────

const PAGE_SIZE = 50;

export default function AdminUsers() {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<FilterChipValue>('all');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [editingUser, setEditingUser] = useState<AdminUser | null>(null);
  const [approvingUser, setApprovingUser] = useState<AdminUser | null>(null);
  const [detailUser, setDetailUser] = useState<AdminUser | null>(null);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  // TESTING-PHASE TOOL — rides the legacy monthly_eval_limit system; delete when the credit ledger fully replaces it
  // Channel filter + per-row eval-limit actions (credit-system testing tools).
  const [channels, setChannels] = useState<AdminChannel[]>([]);
  const [channelFilter, setChannelFilter] = useState<string>(''); // channel slug ('' = all)
  const [channelRoleFilter, setChannelRoleFilter] = useState<string>(''); // '' = any role
  const [limitUser, setLimitUser] = useState<AdminUser | null>(null);
  const [resetUsageUser, setResetUsageUser] = useState<AdminUser | null>(null);
  const [resettingUsage, setResettingUsage] = useState(false);

  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const showToast = useCallback((message: string, type: 'success' | 'error' = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 5000);
  }, []);

  // Debounce the search string
  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 300);
    return () => {
      if (searchTimer.current) clearTimeout(searchTimer.current);
    };
  }, [search]);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, string | number | boolean> = {
        page,
        page_size: PAGE_SIZE,
      };

      // Map filter chips to API params
      if (filter === 'inactive' || filter === 'pending') {
        // Both inactive and pending are determined by is_active=false.
        // Fetch a broad set and filter client-side (minority of users at this scale).
        params.page_size = 200;
      } else if (filter === 'owner_only') {
        params.owner_only = true;
      } else if (filter === 'paying_only') {
        params.paying_only = true;
      } else if (filter !== 'all') {
        params.role = filter;
      }

      if (debouncedSearch) {
        params.search = debouncedSearch;
      }

      // TESTING-PHASE TOOL — rides the legacy monthly_eval_limit system; delete when the credit ledger fully replaces it
      // Channel scoping for the credit-system testing view.
      if (channelFilter) {
        params.channel_slug = channelFilter;
        if (channelRoleFilter) {
          params.channel_role = channelRoleFilter;
        }
      }

      const data = await getUsers(params as Parameters<typeof getUsers>[0]);
      let fetchedUsers = data.users;

      if (filter === 'inactive' || filter === 'pending') {
        // Pending approval = inactive accounts (not yet approved by admin)
        fetchedUsers = fetchedUsers.filter((u) => !u.is_active);
        // Default sort: newest first for the pending view
        if (filter === 'pending') {
          fetchedUsers = [...fetchedUsers].sort(
            (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
          );
        }
      }

      setUsers(fetchedUsers);
      setTotal(filter === 'inactive' || filter === 'pending' ? fetchedUsers.length : data.total);
    } catch {
      showToast('Failed to load users.', 'error');
    } finally {
      setLoading(false);
    }
  }, [filter, page, debouncedSearch, channelFilter, channelRoleFilter, showToast]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // TESTING-PHASE TOOL — rides the legacy monthly_eval_limit system; delete when the credit ledger fully replaces it
  // Load the channel list once for the credit-system testing filter dropdown.
  useEffect(() => {
    let cancelled = false;
    getAllChannels()
      .then((chs) => { if (!cancelled) setChannels(chs); })
      .catch(() => {
        // Non-fatal: the filter dropdown just stays empty.
      });
    return () => { cancelled = true; };
  }, []);

  // TESTING-PHASE TOOL — rides the legacy monthly_eval_limit system; delete when the credit ledger fully replaces it
  const handleLimitSaved = useCallback(
    (updated: AdminUser) => {
      setUsers((prev) => prev.map((u) => (u.id === updated.id ? updated : u)));
      setLimitUser(null);
      showToast(`Evaluation limit updated for ${updated.email}.`);
    },
    [showToast],
  );

  // TESTING-PHASE TOOL — rides the legacy monthly_eval_limit system; delete when the credit ledger fully replaces it
  const handleConfirmResetUsage = useCallback(async () => {
    if (!resetUsageUser) return;
    setResettingUsage(true);
    try {
      const result = await resetEvalUsage(resetUsageUser.id);
      setUsers((prev) =>
        prev.map((u) =>
          u.id === resetUsageUser.id
            ? { ...u, monthly_eval_used: result.monthly_eval_used, monthly_eval_limit: result.monthly_eval_limit }
            : u,
        ),
      );
      showToast(`Eval usage reset for ${resetUsageUser.email}.`);
      setResetUsageUser(null);
    } catch {
      showToast('Failed to reset eval usage.', 'error');
    } finally {
      setResettingUsage(false);
    }
  }, [resetUsageUser, showToast]);

  const handleSaved = useCallback(
    (updated: AdminUser) => {
      setUsers((prev) => prev.map((u) => (u.id === updated.id ? updated : u)));
      setEditingUser(null);
      showToast(`User ${updated.email} updated.`);
    },
    [showToast]
  );

  const handleToggleActive = useCallback(
    async (user: AdminUser) => {
      const nextState = !user.is_active;
      const verb = nextState ? 'Activate' : 'Deactivate';
      if (!window.confirm(`${verb} account for ${user.email}?`)) return;
      try {
        const updated = await updateUser(user.id, { is_active: nextState });
        setUsers((prev) => prev.map((u) => (u.id === updated.id ? updated : u)));
        showToast(`${user.email} ${nextState ? 'activated' : 'deactivated'}.`);
      } catch {
        showToast(`Failed to ${verb.toLowerCase()} user.`, 'error');
      }
    },
    [showToast]
  );

  const handleApproved = useCallback(
    (updated: AdminUser) => {
      // After approval the user becomes active — remove from pending view, update elsewhere
      setUsers((prev) => prev.map((u) => (u.id === updated.id ? updated : u)));
      setApprovingUser(null);
      showToast(`${updated.email} approved and activated.`);
      // Refresh to ensure pending filter stays consistent
      fetchUsers();
    },
    [showToast, fetchUsers]
  );

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const cellStyle: React.CSSProperties = {
    padding: '12px 16px',
    fontSize: '13px',
    color: 'var(--color-text-secondary)',
    borderBottom: '1px solid var(--color-border-subtle)',
    verticalAlign: 'middle',
    whiteSpace: 'nowrap',
  };

  const headerCellStyle: React.CSSProperties = {
    padding: '10px 16px',
    fontSize: '11px',
    fontWeight: 600,
    letterSpacing: '0.06em',
    textTransform: 'uppercase' as const,
    color: 'var(--color-text-muted)',
    borderBottom: '1px solid var(--color-border-subtle)',
    whiteSpace: 'nowrap',
  };

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '32px 24px' }}>
      {/* Page header */}
      <div style={{ marginBottom: '28px' }}>
        <h1
          style={{
            fontFamily: 'var(--font-display)',
            fontWeight: 600,
            fontSize: '28px',
            color: 'var(--color-text-primary)',
            margin: 0,
          }}
        >
          Users
        </h1>
        <p style={{ marginTop: '6px', fontSize: '14px', color: 'var(--color-text-secondary)' }}>
          Manage platform members, roles, and limits
        </p>
      </div>

      {/* Controls row */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '16px',
          marginBottom: '20px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <FilterChips value={filter} onChange={(v) => { setFilter(v); setPage(1); }} />

          {/* TESTING-PHASE TOOL — rides the legacy monthly_eval_limit system; delete when the credit ledger fully replaces it */}
          {/* Channel filter dropdown (credit-system testing view) */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <select
              aria-label="Filter by channel"
              value={channelFilter}
              onChange={(e) => { setChannelFilter(e.target.value); setPage(1); }}
              style={{
                height: '32px',
                borderRadius: '20px',
                border: '1px solid var(--color-border-subtle)',
                backgroundColor: 'var(--color-dark-elevated-2)',
                color: 'var(--color-text-secondary)',
                fontSize: '13px',
                fontWeight: 500,
                padding: '0 10px',
                outline: 'none',
                cursor: 'pointer',
              }}
            >
              <option value="">All channels</option>
              {channels.map((ch) => (
                <option key={ch.id} value={ch.slug}>
                  {ch.name || ch.slug}
                </option>
              ))}
            </select>
            {channelFilter && (
              <select
                aria-label="Filter by channel role"
                value={channelRoleFilter}
                onChange={(e) => { setChannelRoleFilter(e.target.value); setPage(1); }}
                style={{
                  height: '32px',
                  borderRadius: '20px',
                  border: '1px solid var(--color-border-subtle)',
                  backgroundColor: 'var(--color-dark-elevated-2)',
                  color: 'var(--color-text-secondary)',
                  fontSize: '13px',
                  fontWeight: 500,
                  padding: '0 10px',
                  outline: 'none',
                  cursor: 'pointer',
                }}
              >
                <option value="">Any role</option>
                <option value="owner">Owner</option>
                <option value="channel_admin">Channel admin</option>
                <option value="channel_coach">Channel coach</option>
                <option value="coach">Coach</option>
                <option value="athlete">Athlete</option>
                <option value="viewer">Viewer</option>
              </select>
            )}
          </div>
        </div>

        <div style={{ position: 'relative', width: '260px' }}>
          <MagnifyingGlassIcon
            style={{
              position: 'absolute',
              left: '10px',
              top: '50%',
              transform: 'translateY(-50%)',
              width: '16px',
              height: '16px',
              color: 'var(--color-text-muted)',
              pointerEvents: 'none',
            }}
          />
          <input
            type="search"
            placeholder="Search email or name…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{
              width: '100%',
              height: '38px',
              borderRadius: '10px',
              border: '1px solid var(--color-border-subtle)',
              backgroundColor: 'var(--color-dark-elevated-2)',
              color: 'var(--color-text-primary)',
              paddingLeft: '34px',
              paddingRight: '12px',
              fontSize: '13px',
              outline: 'none',
            }}
          />
        </div>
      </div>

      {/* Users table */}
      <div
        style={{
          backgroundColor: 'var(--color-dark-elevated-2)',
          border: '1px solid var(--color-border-subtle)',
          borderRadius: '14px',
          overflow: 'hidden',
        }}
      >
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '700px' }}>
            <thead>
              <tr style={{ backgroundColor: 'var(--color-dark-elevated-1)' }}>
                <th style={{ ...headerCellStyle, textAlign: 'left' }}>Display Name</th>
                <th style={{ ...headerCellStyle, textAlign: 'left' }}>Email</th>
                <th style={{ ...headerCellStyle, textAlign: 'left' }}>Role</th>
                <th style={{ ...headerCellStyle, textAlign: 'left' }}>Subscription</th>
                <th style={{ ...headerCellStyle, textAlign: 'left' }}>Coach Tier</th>
                {/* TESTING-PHASE TOOL — rides the legacy monthly_eval_limit system; delete when the credit ledger fully replaces it */}
                <th style={{ ...headerCellStyle, textAlign: 'left' }}>Evals (used/limit)</th>
                <th style={{ ...headerCellStyle, textAlign: 'left' }}>Active</th>
                <th style={{ ...headerCellStyle, textAlign: 'left' }}>Joined</th>
                <th style={{ ...headerCellStyle, textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td
                    colSpan={9}
                    style={{ ...cellStyle, textAlign: 'center', padding: '40px', color: 'var(--color-text-muted)' }}
                  >
                    Loading…
                  </td>
                </tr>
              ) : users.length === 0 ? (
                <tr>
                  <td
                    colSpan={9}
                    style={{ ...cellStyle, textAlign: 'center', padding: '40px', color: 'var(--color-text-muted)' }}
                  >
                    No users found
                  </td>
                </tr>
              ) : (
                users.map((user) => (
                  <tr key={user.id}>
                    {/* Display name */}
                    <td
                      style={{
                        ...cellStyle,
                        color: 'var(--color-text-primary)',
                        fontWeight: 500,
                        maxWidth: '160px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {user.display_name ?? <span style={{ color: 'var(--color-text-muted)', fontStyle: 'italic' }}>—</span>}
                    </td>

                    {/* Email */}
                    <td
                      style={{
                        ...cellStyle,
                        maxWidth: '220px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {user.email}
                    </td>

                    {/* Role */}
                    <td style={cellStyle}>
                      <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '4px' }}>
                        <RoleBadge role={user.role} />
                        <OwnershipBadge user={user} />
                      </div>
                    </td>

                    {/* Subscription tier */}
                    <td style={cellStyle}>
                      {user.subscription_tier != null ? (
                        <span>Tier {user.subscription_tier}</span>
                      ) : (
                        <span style={{ color: 'var(--color-text-muted)' }}>—</span>
                      )}
                    </td>

                    {/* Coach tier */}
                    <td style={cellStyle}>
                      {user.coach_tier ? (
                        <span style={{ textTransform: 'capitalize' }}>{user.coach_tier}</span>
                      ) : (
                        <span style={{ color: 'var(--color-text-muted)' }}>—</span>
                      )}
                    </td>

                    {/* TESTING-PHASE TOOL — rides the legacy monthly_eval_limit system; delete when the credit ledger fully replaces it */}
                    {/* Evals (used / limit) */}
                    <td style={cellStyle}>
                      <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                        {user.monthly_eval_used ?? 0}
                        {' / '}
                        {user.monthly_eval_limit != null ? user.monthly_eval_limit : '∞'}
                      </span>
                    </td>

                    {/* Active */}
                    <td style={cellStyle}>
                      <span
                        style={{
                          display: 'inline-block',
                          width: '8px',
                          height: '8px',
                          borderRadius: '50%',
                          backgroundColor: user.is_active ? '#34D399' : '#F87171',
                          marginRight: '6px',
                        }}
                      />
                      {user.is_active ? 'Yes' : 'No'}
                    </td>

                    {/* Joined */}
                    <td style={cellStyle}>{formatDate(user.created_at)}</td>

                    {/* Actions */}
                    <td style={{ ...cellStyle, textAlign: 'right' }}>
                      <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end' }}>
                        {/* Approve button — only for inactive (pending approval) users */}
                        {!user.is_active && (
                          <button
                            type="button"
                            title="Approve account"
                            onClick={() => setApprovingUser(user)}
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px',
                              padding: '4px 10px',
                              borderRadius: '6px',
                              border: '1px solid rgba(52,211,153,0.4)',
                              backgroundColor: 'rgba(52,211,153,0.08)',
                              color: '#34D399',
                              fontSize: '12px',
                              cursor: 'pointer',
                              whiteSpace: 'nowrap',
                              fontWeight: 500,
                            }}
                          >
                            <CheckCircleIcon style={{ width: '13px', height: '13px' }} />
                            Approve
                          </button>
                        )}
                        <button
                          type="button"
                          title="View user details"
                          aria-label="View user details"
                          onClick={() => setDetailUser(user)}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                            padding: '4px 10px',
                            borderRadius: '6px',
                            border: '1px solid rgba(96,165,250,0.3)',
                            backgroundColor: 'rgba(96,165,250,0.06)',
                            color: 'var(--color-accent-blue, #60A5FA)',
                            fontSize: '12px',
                            cursor: 'pointer',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          Details
                        </button>
                        <button
                          type="button"
                          title="Edit user"
                          onClick={() => setEditingUser(user)}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                            padding: '4px 10px',
                            borderRadius: '6px',
                            border: '1px solid var(--color-border-subtle)',
                            backgroundColor: 'transparent',
                            color: 'var(--color-text-secondary)',
                            fontSize: '12px',
                            cursor: 'pointer',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          <PencilSquareIcon style={{ width: '13px', height: '13px' }} />
                          Edit
                        </button>
                        {/* TESTING-PHASE TOOL — rides the legacy monthly_eval_limit system; delete when the credit ledger fully replaces it */}
                        <button
                          type="button"
                          title="Set evaluation limit"
                          onClick={() => setLimitUser(user)}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                            padding: '4px 10px',
                            borderRadius: '6px',
                            border: '1px solid var(--color-border-subtle)',
                            backgroundColor: 'transparent',
                            color: 'var(--color-text-secondary)',
                            fontSize: '12px',
                            cursor: 'pointer',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          Set limit
                        </button>
                        {/* TESTING-PHASE TOOL — rides the legacy monthly_eval_limit system; delete when the credit ledger fully replaces it */}
                        <button
                          type="button"
                          title="Reset monthly eval usage"
                          onClick={() => setResetUsageUser(user)}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                            padding: '4px 10px',
                            borderRadius: '6px',
                            border: '1px solid rgba(244,201,93,0.3)',
                            backgroundColor: 'transparent',
                            color: 'var(--color-accent-gold)',
                            fontSize: '12px',
                            cursor: 'pointer',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          Reset used
                        </button>
                        <button
                          type="button"
                          title={user.is_active ? 'Deactivate account' : 'Activate account'}
                          onClick={() => handleToggleActive(user)}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                            padding: '4px 10px',
                            borderRadius: '6px',
                            border: user.is_active
                              ? '1px solid rgba(248,113,113,0.3)'
                              : '1px solid rgba(52,211,153,0.3)',
                            backgroundColor: 'transparent',
                            color: user.is_active ? '#F87171' : '#34D399',
                            fontSize: '12px',
                            cursor: 'pointer',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {user.is_active ? 'Deactivate' : 'Activate'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '12px 20px',
              borderTop: '1px solid var(--color-border-subtle)',
            }}
          >
            <span style={{ fontSize: '13px', color: 'var(--color-text-muted)' }}>
              Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} of {total}
            </span>
            <div style={{ display: 'flex', gap: '8px' }}>
              <Button
                variant="secondary"
                size="sm"
                disabled={page === 1}
                onClick={() => setPage((p) => p - 1)}
              >
                Previous
              </Button>
              <Button
                variant="secondary"
                size="sm"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Detail modal */}
      {detailUser && (
        <UserDetailModal
          user={detailUser}
          onClose={() => setDetailUser(null)}
        />
      )}

      {/* Edit modal */}
      {editingUser && (
        <EditUserModal
          user={editingUser}
          onClose={() => setEditingUser(null)}
          onSaved={handleSaved}
        />
      )}

      {/* Approve modal */}
      {approvingUser && (
        <ApproveUserModal
          user={approvingUser}
          onClose={() => setApprovingUser(null)}
          onApproved={handleApproved}
        />
      )}

      {/* TESTING-PHASE TOOL — rides the legacy monthly_eval_limit system; delete when the credit ledger fully replaces it */}
      {/* Set eval-limit modal */}
      {limitUser && (
        <SetLimitModal
          user={limitUser}
          onClose={() => setLimitUser(null)}
          onSaved={handleLimitSaved}
        />
      )}

      {/* TESTING-PHASE TOOL — rides the legacy monthly_eval_limit system; delete when the credit ledger fully replaces it */}
      {/* Reset eval-usage confirmation */}
      <ConfirmationDialog
        isOpen={resetUsageUser != null}
        onClose={() => { if (!resettingUsage) setResetUsageUser(null); }}
        onConfirm={handleConfirmResetUsage}
        title="Reset monthly eval usage"
        description={
          resetUsageUser
            ? `Reset this month's evaluation usage counter to 0 for ${resetUsageUser.email}? This does not change their limit.`
            : ''
        }
        confirmLabel="Reset usage"
        variant="warning"
        isLoading={resettingUsage}
      />

      {/* Toast notification */}
      {toast && (
        <Toast message={toast.message} type={toast.type} onDismiss={() => setToast(null)} />
      )}
    </div>
  );
}
